import { useMutation, useQuery } from '@apollo/client'
import { useDebounce, useForm, useTranslate } from '@ur-solutions/react-hooks'
import { Input } from 'components/Input'
import { SortableHeader, Table } from 'components/Table'
import { ModuleTitle } from 'components/Title'
import { Navigate } from 'react-router'
import styled from 'styled-components'
import { useNodes, useOnErrorAuto, useParam, useUser } from 'utils/hooks'
import { overloadColor } from 'utils/style'
import { SALARY_BASE, SALARY_BASE_ENTRIES } from './queries'
import { EXPORT_SALARY_BASE } from './mutations'
import { ExportFormat } from 'modules/exports'
import { Loader } from '@ur-solutions/react-components'
import { Button } from 'components'

const Wrapper = styled.div`
  ${props => props.theme.layout.default};
`

const StyledTable = styled(Table)`
  table-layout: fixed;
  td {
    width: 20%;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`

const StyledRow = styled.tr<{ background?: string }>`
  background-color: ${props =>
    props.background ? overloadColor(props.background) : 'auto'};
`

const TableFilter = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding: 1rem 0;
  background-color: ${props => props.theme.colors.white};
  border-radius: 16px;

  & > div:first-of-type {
    transform: translateX(1rem);
  }
  & > div:last-of-type {
    transform: translateX(-1rem);
  }
`

const FilterBlock = styled.div`
  flex-basis: 0;
  flex: 1 1 0px;
  padding: 0 1rem;
  & > label {
    font-size: 0.9rem;
    font-weight: 600;
    margin: 0 0 6px 6px;
    color: ${props => props.theme.colors.gray5};
  }
`

export const SalaryBase: React.CFC = () => {
  const translations = useTranslate({
    name: 'common.name',
    created: 'common.created',
    edit: 'common.edit',
    delete: 'common.delete',
    user: 'common.user',

    filter: 'common.filter',

    userFilter: 'common.user-filter',
    departmentFilter: 'common.department-filter',
    salaryCodeFilter: 'common.salary-code-filter',

    salaryBase: 'common.salary-base',
    salaryBaseLines: 'salary.salary-base-lines',
    externalId: 'common.external-id',
    salaryCode: 'common.salary-code',
    amount: 'common.amount',
    initialAmount: 'salary.initial-amount',
    department: 'common.department',
    export: 'common.export',
  })

  const salaryBaseId = useParam('salaryBaseId')
  const me = useUser()

  const { formValues, formChangeHandler: handler } = useForm({
    values: {
      qUser: '',
      qDepartment: '',
      qSalaryCode: '',
      orderBy: 'user__lastName',
    },
  })

  const debouncedUser = useDebounce(formValues.qUser, 300)
  const debouncedDepartment = useDebounce(formValues.qDepartment, 300)
  const debouncedSalaryCode = useDebounce(formValues.qSalaryCode, 300)

  const onErrorAuto = useOnErrorAuto()

  const { data, loading } = useQuery(SALARY_BASE, {
    variables: {
      id: salaryBaseId,
    },
    fetchPolicy: 'cache-and-network',
  })

  const { data: entryData, loading: entryLoading } = useQuery(
    SALARY_BASE_ENTRIES,
    {
      variables: {
        id: salaryBaseId,
        qUser: debouncedUser,
        qDepartment: debouncedDepartment,
        qSalaryCode: debouncedSalaryCode,
        orderBy: formValues.orderBy,
      },
      fetchPolicy: 'cache-and-network',
    }
  )

  const [exportSalaryBase, { loading: exportLoading }] = useMutation(
    EXPORT_SALARY_BASE,
    {
      onError: onErrorAuto(),
    }
  )

  const salaryBase = data?.salaryBase
  const entries = useNodes(entryData?.allSalaryBaseEntries)

  if (!loading && !salaryBase) return <Navigate to="/salary-bases" />

  return (
    <Wrapper>
      <TitleWrapper>
        <ModuleTitle>{salaryBase?.name}</ModuleTitle>
        {entryLoading ? (
          <Loader.Spinner size={24} />
        ) : (
          <Button
            onClick={() =>
              exportSalaryBase({
                variables: {
                  salaryBase: salaryBaseId,
                  user: me.id,
                  exportFormat: ExportFormat.VISMA,
                },
              })
            }
            disabled={exportLoading}
          >
            {translations.export}
          </Button>
        )}
      </TitleWrapper>

      <TableFilter>
        <FilterBlock>
          <label>{translations.userFilter}</label>
          <Input
            type="text"
            value={formValues.qUser}
            onChange={handler('qUser')}
            grayed
          />
        </FilterBlock>
        <FilterBlock>
          <label>{translations.departmentFilter}</label>
          <Input
            type="text"
            value={formValues.qDepartment}
            onChange={handler('qDepartment')}
            grayed
          />
        </FilterBlock>
        <FilterBlock>
          <label>{translations.salaryCodeFilter}</label>
          <Input
            type="text"
            value={formValues.qSalaryCode}
            onChange={handler('qSalaryCode')}
            grayed
          />
        </FilterBlock>
        <FilterBlock></FilterBlock>
        <FilterBlock></FilterBlock>
      </TableFilter>

      <StyledTable noBorders rowHoverBackground="gray10">
        <thead>
          <tr>
            <SortableHeader
              baseValue="user__lastName"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.user}
            </SortableHeader>
            <SortableHeader
              baseValue="departmentId"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.department}
            </SortableHeader>
            <SortableHeader
              baseValue="salaryId"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.externalId}
            </SortableHeader>
            <SortableHeader
              baseValue="amount"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.amount}
            </SortableHeader>
            <SortableHeader
              baseValue="initialAmount"
              sortValue={formValues.orderBy}
              invertDirection
              onSort={handler('orderBy')}
            >
              {translations.initialAmount}
            </SortableHeader>
          </tr>
        </thead>
        <tbody>
          {entries.map(entry => {
            const userText = entry.user.internalNumber
              ? `(${entry.user.internalNumber}) ${entry.user.fullName}`
              : entry.user.fullName
            return (
              <StyledRow
                key={entry.id}
                background={entry.departmentId === '' ? 'red12' : undefined}
              >
                <td>{userText}</td>
                <td>
                  <Input
                    type="text"
                    value={entry.departmentId}
                    onChange={() => {}}
                    disabled
                    grayed
                  />
                </td>
                <td>
                  <Input
                    type="text"
                    value={entry.salaryId}
                    onChange={() => {}}
                    disabled
                    grayed
                  />
                </td>
                <td>
                  <Input
                    type="text"
                    value={entry.amount.toString()}
                    onChange={() => {}}
                    disabled
                    grayed
                  />
                </td>
                <td>{entry.initialAmount}</td>
              </StyledRow>
            )
          })}
        </tbody>
      </StyledTable>
    </Wrapper>
  )
}
